import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { ExpenseApprovalItem, ExpenseApprovalService } from './expense-approval.service';

type ExpenseApprovalRow = Omit<ExpenseApprovalItem, 'bookingTotal' | 'amount'> & {
  bookingTotal: number;
  amount: number;
};

@Component({
  selector: 'app-expense-approval-page',
  imports: [DatePipe, DecimalPipe, LucideAngularModule, StatusBadge, RefreshButtonComponent, TableModule, InputTextModule],
  templateUrl: './expense-approval-page.component.html',
})
export class ExpenseApprovalPageComponent implements OnInit {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly expenseApprovalService = inject(ExpenseApprovalService);
  readonly rows = signal<ExpenseApprovalRow[]>([]);
  readonly isLoadingExpenses = signal(false);
  readonly selectedExpense = signal<ExpenseApprovalRow | null>(null);

  ngOnInit(): void {
    void this.loadExpenses();
  }

  async loadExpenses(): Promise<void> {
    if (this.isLoadingExpenses()) return;
    this.isLoadingExpenses.set(true);
    this.overlay.showLoading('กำลังโหลดใบขอเบิกรออนุมัติ...');
    try {
      const expenses = await this.expenseApprovalService.getPending();
      this.rows.set(expenses.map((expense) => ({
        ...expense,
        bookingTotal: Number(expense.bookingTotal),
        amount: Number(expense.amount),
      })));
    } catch (error: unknown) {
      const message = error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
        ? error.error.message : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('โหลดใบขอเบิกไม่สำเร็จ', message);
    } finally {
      this.isLoadingExpenses.set(false);
      this.overlay.hideLoading();
    }
  }
  openExpenseDetail(row: ExpenseApprovalRow): void {
    this.selectedExpense.set(row);
  }
  closeExpenseDetail(): void {
    this.selectedExpense.set(null);
  }

  openAttachment(fileUrl: string | null): void {
    if (!fileUrl) return;
    window.open(this.expenseApprovalService.resolveFileUrl(fileUrl), '_blank', 'noopener,noreferrer');
  }

  async approve(row: ExpenseApprovalRow): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'อนุมัติใบขอเบิก',
      message: `ยืนยันอนุมัติ ${row.code} จำนวน ${row.amount.toLocaleString()} บาท`,
      confirmText: 'อนุมัติ',
    });
    if (!confirmed) return;
    this.overlay.showLoading('กำลังอนุมัติใบขอเบิก...');
    try {
      await this.expenseApprovalService.approve(row.id);
      this.rows.update((rows) => rows.filter((item) => item.id !== row.id));
      this.closeExpenseDetail();
      this.overlay.success('อนุมัติสำเร็จ', `${row.code} ถูกส่งให้ฝ่ายบัญชีตรวจสอบแล้ว`);
    } catch (error: unknown) {
      const message = error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
        ? error.error.message : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('อนุมัติไม่สำเร็จ', message);
    }
  }

  async reject(row: ExpenseApprovalRow): Promise<void> {
    const confirmed = await this.overlay.confirm({
      title: 'ไม่อนุมัติใบขอเบิก',
      message: `ยืนยันไม่อนุมัติ ${row.code} หรือไม่`,
      confirmText: 'ไม่อนุมัติ',
    });
    if (!confirmed) return;
    this.overlay.showLoading('กำลังบันทึกผลการอนุมัติ...');
    try {
      await this.expenseApprovalService.reject(row.id);
      this.rows.update((rows) => rows.filter((item) => item.id !== row.id));
      this.closeExpenseDetail();
      this.overlay.success('บันทึกผลแล้ว', `${row.code} ไม่ผ่านการอนุมัติ`);
    } catch (error: unknown) {
      const message = error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
        ? error.error.message : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('บันทึกผลไม่สำเร็จ', message);
    }
  }
}
